<script lang="ts">
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import {
    mdiCalendarCheckOutline,
    mdiCalendarPlusOutline,
    mdiCalendarRemoveOutline,
    mdiCalendarSyncOutline,
    mdiCheckBold,
    mdiClipboardTextClockOutline,
    mdiArchiveOutline,
  } from "@mdi/js"
  import type { WorkOrderRow } from "../../../../../../_shared/_logic/work-order-table-model"
  import {
    formatBatchActionCount,
    resolveWorkOrderBatchActionCounts,
    resolveWorkOrderBatchActionDisabledReasons,
    resolveWorkOrderBatchActionTargets,
  } from "./_logic/batchActionEligibility"
  import {
    WORK_ORDER_ACTION_TOOLTIP_CLASS,
    workOrderActionTooltipWithDisabledReason,
    workOrderActionTooltips,
  } from "../../../../../../../../_shared/_logic/components-work-orders-action-tooltips"
  import type { TableActionRunner } from "../../row-action-commands"
  import {
    completeSelectedWorkOrders,
    archiveSelectedWorkOrders,
    openCancelAnnouncementPlansDialog,
    openCancelReplanDialog,
    openConfirmAppointmentsDialog,
    openCreateAnnouncementPlansDialog,
    openCreateAppointmentsDialog,
    type BatchActionCommandContext,
  } from "./_actions/batchActionCommands"

  type Props = {
    organizationId: string
    selectedRows: unknown[]
    selectedCount?: number
    actionLoading: boolean
    scopedCanEdit: boolean
    canPlanAppointments: boolean
    relevantScheduleEventByWorkOrderId: Record<string, ScheduleEvent>
    activeContactOrderByWorkOrderId: Record<string, ContactOrder>
    properties: Property[]
    customers: Customer[]
    contactPoints: ContactPoint[]
    contactOrders: ContactOrder[]
    employees: { id: string; label: string }[]
    workingHoursByUser: Record<string, any>
    planningSettings: PlanningSettings | null
    trialRestrictionsActive?: boolean | null
    runTableAction: TableActionRunner
    clearSelection: () => void
    onChanged?: () => Promise<void> | void
    onScheduleEventsUpdated?: (events: ScheduleEvent[]) => Promise<void> | void
  }

  const {
    organizationId,
    selectedRows,
    selectedCount,
    actionLoading,
    scopedCanEdit,
    canPlanAppointments,
    relevantScheduleEventByWorkOrderId,
    activeContactOrderByWorkOrderId,
    properties,
    customers,
    contactPoints,
    contactOrders,
    employees,
    workingHoursByUser,
    planningSettings,
    trialRestrictionsActive = false,
    runTableAction,
    clearSelection,
    onChanged = async () => {},
    onScheduleEventsUpdated = async () => {},
  }: Props = $props()

  const targets = $derived(
    resolveWorkOrderBatchActionTargets({
      rows: selectedRows,
      relevantScheduleEventByWorkOrderId,
      activeContactOrderByWorkOrderId,
    }),
  )
  const counts = $derived(resolveWorkOrderBatchActionCounts(targets))
  const disabledReasons = $derived(
    resolveWorkOrderBatchActionDisabledReasons(counts, { targets, trialRestrictionsActive }),
  )
  const selectionLabel = $derived(
    `${new Intl.NumberFormat("de-DE").format(selectedCount ?? selectedRows.length)} ausgewählt`,
  )
  const selectedWorkOrderRows = $derived(selectedRows as WorkOrderRow[])
  const archivableCount = $derived(
    selectedWorkOrderRows.filter((row) => !row.workOrder?.archived_at).length,
  )
  const commandContext: BatchActionCommandContext = $derived({
    organizationId,
    actionLoading,
    scopedCanEdit,
    relevantScheduleEventByWorkOrderId,
    properties,
    customers,
    contactPoints,
    contactOrders,
    employees,
    workingHoursByUser,
    planningSettings,
    trialRestrictionsActive,
    runTableAction,
    onChanged,
    onScheduleEventsUpdated,
  })
</script>

<ActionBox label={selectionLabel} darkMode={true}>
  <section class="batch-action-row" aria-label="Auftragsaktionen">
    <div class="batch-action-label">
      <strong>Aufträge</strong>
    </div>
    <div class="batch-action-buttons">
      <CTA
        type="secondaryButton"
        small={true}
        darkMode={false}
        content={`erledigt (${formatBatchActionCount(counts.selected)})`}
        tooltipContent={workOrderActionTooltipWithDisabledReason(
          workOrderActionTooltips.batchComplete,
          disabledReasons.complete,
        )}
        tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
        disabled={actionLoading || !counts.selected}
        onClick={() =>
          completeSelectedWorkOrders({
            rows: selectedRows,
            actionLoading,
            scopedCanEdit,
            runTableAction,
            clearSelection,
            onChanged,
          })}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiCheckBold} />
        {/snippet}
      </CTA>

      <CTA
        type="secondaryButton"
        small={true}
        darkMode={false}
        content={`archivieren (${formatBatchActionCount(archivableCount)})`}
        tooltipContent={workOrderActionTooltipWithDisabledReason(
          workOrderActionTooltips.batchArchive,
          !archivableCount ? "Keine aktiven Aufträge ausgewählt." : null,
        )}
        tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
        disabled={actionLoading || !archivableCount}
        onClick={() =>
          archiveSelectedWorkOrders({
            rows: selectedRows,
            actionLoading,
            scopedCanEdit,
            runTableAction,
            clearSelection,
            onChanged,
          })}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiArchiveOutline} />
        {/snippet}
      </CTA>
    </div>
  </section>

  <section class="batch-action-row" aria-label="Terminaktionen">
    <div class="batch-action-label">
      <strong>Termine</strong>
    </div>
    <div class="batch-action-buttons">
      <CTA
        type="secondaryButton"
        small={true}
        darkMode={false}
        content={`Verschieben (${formatBatchActionCount(counts.movable)})`}
        tooltipContent={workOrderActionTooltipWithDisabledReason(
          workOrderActionTooltips.batchReschedule,
          disabledReasons.reschedule,
        )}
        tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
        disabled={actionLoading || !canPlanAppointments || !counts.movable}
        onClick={() => openCancelReplanDialog(commandContext, targets.movable, clearSelection)}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiCalendarSyncOutline} />
        {/snippet}
      </CTA>
      <CTA
        type="secondaryButton"
        small={true}
        darkMode={false}
        content={`Bestätigen (${formatBatchActionCount(counts.confirmable)})`}
        tooltipContent={workOrderActionTooltipWithDisabledReason(
          workOrderActionTooltips.batchConfirmAppointments,
          disabledReasons.confirmAppointments,
        )}
        tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
        disabled={actionLoading || !canPlanAppointments || !counts.confirmable}
        onClick={() =>
          openConfirmAppointmentsDialog(commandContext, targets.confirmable, clearSelection)}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiCalendarCheckOutline} />
        {/snippet}
      </CTA>
      <CTA
        type="secondaryButton"
        small={true}
        darkMode={false}
        content={`Anlegen (${formatBatchActionCount(counts.appointmentCreatable)})`}
        tooltipContent={workOrderActionTooltipWithDisabledReason(
          workOrderActionTooltips.batchCreateAppointments,
          disabledReasons.createAppointments,
        )}
        tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
        disabled={actionLoading || !canPlanAppointments || !counts.appointmentCreatable}
        onClick={() =>
          openCreateAppointmentsDialog(
            commandContext,
            targets.appointmentCreatable,
            clearSelection,
          )}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiCalendarPlusOutline} />
        {/snippet}
      </CTA>
    </div>
  </section>

  <section class="batch-action-row" aria-label="Ankündigungsplan-Aktionen">
    <div class="batch-action-label">
      <strong>Ankündigungsplan</strong>
    </div>
    <div class="batch-action-buttons">
      <CTA
        type="secondaryButton"
        small={true}
        darkMode={false}
        content={`Anlegen (${formatBatchActionCount(counts.announcementCreatable)})`}
        tooltipContent={workOrderActionTooltipWithDisabledReason(
          workOrderActionTooltips.batchCreateAnnouncements,
          disabledReasons.createAnnouncements,
        )}
        tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
        disabled={actionLoading || !canPlanAppointments || !counts.announcementCreatable}
        onClick={() =>
          openCreateAnnouncementPlansDialog(
            commandContext,
            targets.announcementCreatable,
            clearSelection,
          )}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiClipboardTextClockOutline} />
        {/snippet}
      </CTA>
      <CTA
        type="secondaryButton"
        small={true}
        darkMode={false}
        content={`Beenden (${formatBatchActionCount(counts.announcementCancelable)})`}
        tooltipContent={workOrderActionTooltipWithDisabledReason(
          workOrderActionTooltips.batchCancelAnnouncements,
          disabledReasons.cancelAnnouncements,
        )}
        tooltipClassName={WORK_ORDER_ACTION_TOOLTIP_CLASS}
        disabled={actionLoading || !canPlanAppointments || !counts.announcementCancelable}
        onClick={() =>
          openCancelAnnouncementPlansDialog(
            commandContext,
            targets.announcementCancelable,
            clearSelection,
          )}
      >
        {#snippet iconSnippet()}
          <Icon path={mdiCalendarRemoveOutline} />
        {/snippet}
      </CTA>
    </div>
  </section>
</ActionBox>

<style>
  .batch-action-row {
    display: grid;
    grid-template-columns: minmax(10rem, 13rem) minmax(0, 1fr);
    gap: 1rem;
    align-items: center;
    padding: 0.55rem 0;
  }

  .batch-action-row + .batch-action-row {
    border-top: 1px solid color-mix(in srgb, var(--text-invers-100) 18%, transparent);
  }

  .batch-action-label {
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
    color: var(--text-invers-100);
    line-height: 1.2;
  }

  .batch-action-label strong {
    font-size: 0.78rem;
    text-transform: uppercase;
  }

  .batch-action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    align-items: center;
    justify-content: flex-end;
  }

  @media (max-width: 760px) {
    .batch-action-row {
      grid-template-columns: 1fr;
      gap: 0.45rem;
    }
  }
</style>
